import {MaterialNode} from "#material/nodes/base/material/material-node"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"
import type {EmissionSockets} from "#material/nodes/shader/emission/definition"
import {emissionDefinition} from "#material/nodes/shader/emission/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {asVec3, threeConvert, threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import {cyclesColor} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {EmissionNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class Emission extends MaterialNode<EmissionSockets> {
    public override get definition() {
        return emissionDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<EmissionSockets>> => {
        const material = new ThreeColormassMaterial()

        const {color, strength} = await evaluator.getAll({
            color: this.parameters.inputs.color,
            strength: this.parameters.inputs.strength,
        })

        material.colorNode = threeRGBColor({r: 0, g: 0, b: 0})

        const emissionValue =
            color?.toColor() ??
            threeConvert(this.parameters.settings.color, threeRGBColor, (val) => val.r > 0 || val.g > 0 || val.b > 0) ??
            material.emissiveNode
        if (emissionValue) {
            const emissionStrengthValue = strength?.toFloat() ?? threeConvert(this.parameters.settings.strength, threeValue)
            if (emissionStrengthValue) material.emissiveNode = TSL.mul(asVec3(emissionValue), emissionStrengthValue)
            else material.emissiveNode = emissionValue
        }

        return {emission: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<EmissionSockets>> => {
        const emissionNode: EmissionNode = {
            type: "emission",
            inputs: await evaluator.getAll({
                color: this.parameters.inputs.color,
                strength: this.parameters.inputs.strength,
                surface_mix_weight: undefined,
            }),
            parameters: {color: cyclesColor(this.parameters.settings.color), strength: this.parameters.settings.strength, surface_mix_weight: undefined},
            resources: {},
        }

        return {emission: [emissionNode, "emission"]}
    }
}
